import type { UPlotSeriesAppearance } from 'features/timeSeries/components/timeSeriesScatterplot/useUPlotGenerators'
import { perfCount, perfMark } from 'features/timeSeries/perf/processDataPerf'
import { logAnnotationDebug } from 'features/timeSeries/utils/annotationDebug'
import {
  markProgrammaticXRange,
  notifyChartViewportChanged,
} from 'features/timeSeries/utils/chartViewportSignal'
import {
  CHART_VIEWPORT_TRANSITION_MS,
  interpolateScaleRanges,
  prefersReducedMotion,
  resolveTargetScaleRanges,
  ScaleRanges,
  scaleRangesAreEquivalent,
  shouldAnimateViewportTransition,
} from 'features/timeSeries/utils/chartViewportTransition'
import { buildUPlotScaleRanges } from 'features/timeSeries/utils/uPlotConfig'
import { MutableRefObject, useEffect, useMemo, useRef } from 'react'
import uPlot from 'uplot'

interface UseUPlotChartOptions {
  uPlotOptions: uPlot.Options
  uPlotRecreateKey: string
  uPlotSeriesAppearance: UPlotSeriesAppearance[]
  uPlotData: uPlot.AlignedData
  uPlotRef: MutableRefObject<uPlot | null>
  hasDataPoints: boolean
}

/** Current limits of the scales we are about to drive, as the move's start point. */
const readScaleRanges = (instance: uPlot, scaleKeys: string[]): ScaleRanges => {
  const ranges: ScaleRanges = {}

  scaleKeys.forEach((scaleKey) => {
    const scale = instance.scales[scaleKey]
    if (!scale) return

    const { min, max } = scale
    if (typeof min !== 'number' || typeof max !== 'number') return
    if (!Number.isFinite(min) || !Number.isFinite(max)) return

    ranges[scaleKey] = { min, max }
  })

  return ranges
}

const applyScaleRanges = (instance: uPlot, ranges: ScaleRanges): void => {
  Object.entries(ranges).forEach(([scaleKey, range]) => {
    const scale = instance.scales[scaleKey]
    if (!scale) return
    // Skip a set that lands exactly on the current limits. uPlot only fires
    // its setScale hook when a value actually CHANGES, so a no-op set can
    // never consume the programmatic mark recorded for it — the mark would sit
    // in the registry forever, and a later genuine user zoom landing on the
    // same rounded-ms window would be mistaken for one of ours and swallowed.
    // Exact equality deliberately mirrors uPlot's own `!=` change check.
    if (scale.min === range.min && scale.max === range.max) return
    // Record the x-range we are pushing so its deferred setScale hook is
    // recognised as ours. uPlot's hook cannot tell this from a drag-to-zoom,
    // and it fires on a microtask — after this returns — so the guard has to be
    // value-scoped, not "are we applying right now?". Reporting a transition
    // frame as a user zoom made the transition re-fetch itself once per frame.
    if (scaleKey === 'x') markProgrammaticXRange(range.min, range.max)
    instance.setScale(scaleKey, { min: range.min, max: range.max })
  })

  // The plot moved under any DOM positioned against it. Say so — nothing else
  // will, now that a move no longer means a teardown.
  notifyChartViewportChanged()
}

/**
 * Re-apply a uPlot instance's size from a freshly-observed rect when it becomes
 * visible again, so its cached cursor rect is refreshed.
 *
 * uPlot caches `over.getBoundingClientRect()` and maps every pointer event
 * (including the native drag-to-zoom select) against that cached rect. It
 * re-reads the rect only on a window resize/scroll or on `setSize` — never
 * merely because the element was shown again. Returns whether a resize was
 * applied so the effect wiring can stay a thin shell around a tested decision.
 */
export const resyncViewportRectOnVisible = (
  instance: Pick<uPlot, 'setSize'>,
  entry: Pick<
    IntersectionObserverEntry,
    'isIntersecting' | 'boundingClientRect'
  >
): boolean => {
  if (!entry.isIntersecting) return false

  const { width, height } = entry.boundingClientRect
  if (!(width > 0) || !(height > 0)) return false

  instance.setSize({ width, height })
  return true
}

/**
 * Manages the full uPlot instance lifecycle: creation, data updates, resizing,
 * and drag-to-zoom protection against mid-selection teardown.
 */
export function useUPlotChart({
  uPlotOptions,
  uPlotRecreateKey,
  uPlotSeriesAppearance,
  uPlotData,
  uPlotRef,
  hasDataPoints,
}: UseUPlotChartOptions): {
  chartContainerRef: MutableRefObject<HTMLDivElement | null>
  chartAreaRef: MutableRefObject<HTMLDivElement | null>
} {
  const chartContainerRef = useRef<HTMLDivElement | null>(
    null
  ) as MutableRefObject<HTMLDivElement | null>
  const chartAreaRef = useRef<HTMLDivElement | null>(
    null
  ) as MutableRefObject<HTMLDivElement | null>
  const instanceCountRef = useRef(0)
  const latestOptionsRef = useRef(uPlotOptions)
  const latestSeriesAppearanceRef = useRef(uPlotSeriesAppearance)
  const tweenFrameRef = useRef<number | null>(null)
  const tweenedInstanceRef = useRef<uPlot | null>(null)

  // The limits to drive the live instance to. Derived from the options rather
  // than passed alongside them so there is one source of truth, and so any
  // range the caller layers on top (the axis-drag x preview) is picked up here
  // too. `uPlotOptions` is retained across renders whenever these numbers are
  // unchanged, so its identity is a sound memo key.
  const uPlotScaleRanges = useMemo(
    () => buildUPlotScaleRanges(uPlotOptions.scales ?? {}),
    [uPlotOptions]
  )

  useEffect(() => {
    latestOptionsRef.current = uPlotOptions
  }, [uPlotOptions])

  useEffect(() => {
    latestSeriesAppearanceRef.current = uPlotSeriesAppearance
  }, [uPlotSeriesAppearance])

  useEffect(() => {
    if (!chartContainerRef.current) return

    if (uPlotRef.current) {
      logAnnotationDebug('uplot-destroy-preexisting-instance', {
        reason: 'unexpected-live-instance-before-create',
        seriesCount: Math.max(0, uPlotRef.current.series.length - 1),
      })
      uPlotRef.current.destroy()
      uPlotRef.current = null
      perfCount('uplotDestroyed')
    }

    if (!hasDataPoints) {
      logAnnotationDebug('uplot-create-skipped', {
        reason: 'no-data-points',
      })
      return
    }

    const instance = new uPlot(
      latestOptionsRef.current,
      uPlotData,
      chartContainerRef.current
    )
    instanceCountRef.current += 1
    uPlotRef.current = instance
    perfCount('uplotCreated')
    perfMark('chartPainted')

    logAnnotationDebug('uplot-created', {
      instanceCount: instanceCountRef.current,
      seriesCount: Math.max(0, instance.series.length - 1),
      xValueCount: Array.isArray(uPlotData[0]) ? uPlotData[0].length : 0,
    })

    // Sync to the actual container dimensions. The options may carry a stale
    // width/height from the initial mount measurement, so reading the live
    // ChartArea rect ensures the chart fills its container after recreation
    // (e.g. data reload or LOD change following a sidebar resize).
    if (chartAreaRef.current) {
      const { width, height } = chartAreaRef.current.getBoundingClientRect()
      if (width > 0 && height > 0) {
        instance.setSize({ width, height })
      }
    }

    return () => {
      logAnnotationDebug('uplot-destroyed', {
        instanceCount: instanceCountRef.current,
        seriesCount: Math.max(0, instance.series.length - 1),
      })
      instance.destroy()
      if (uPlotRef.current === instance) {
        uPlotRef.current = null
      }
    }
    // Recreate when options change (includes series, scales, axes, plugins)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uPlotRecreateKey, hasDataPoints])

  // Update the data and move the viewport onto it, in place.
  //
  // This is the path that a range change now takes. It used to be effectively
  // dead: the auto y-range numbers were baked into `uPlotRecreateKey`, so any
  // new window destroyed and rebuilt the instance before this ran (the
  // "pop-in"). Worse, `setData(data, false)` skips uPlot's `autoScaleX`
  // entirely, so on the rare occasions this path *did* run alone it left the
  // new data drawn against the previous window's x scale. The teardown was
  // what moved the x axis; it is not anymore, so the move is explicit here.
  useEffect(() => {
    const cancelTween = () => {
      if (tweenFrameRef.current !== null) {
        cancelAnimationFrame(tweenFrameRef.current)
        tweenFrameRef.current = null
      }
    }

    // Whatever the previous move was heading toward, this one supersedes it.
    // Cancelled before the guard below, not after: when the data empties out,
    // this effect bails but the create effect still tears the instance down,
    // and an in-flight frame would then be driving scales on a destroyed plot.
    cancelTween()

    const instance = uPlotRef.current
    if (!instance || !hasDataPoints) return

    const target = resolveTargetScaleRanges({
      xValues: Array.isArray(uPlotData[0])
        ? (uPlotData[0] as ReadonlyArray<number | null | undefined>)
        : undefined,
      explicitRanges: uPlotScaleRanges,
    })
    const from = readScaleRanges(instance, Object.keys(target))

    // `false` keeps the current viewport: the scales are ours to drive below.
    instance.setData(uPlotData, false)

    const isSameInstance = tweenedInstanceRef.current === instance
    tweenedInstanceRef.current = instance

    if (Object.keys(target).length === 0) {
      notifyChartViewportChanged()
      return
    }

    // A fresh instance has nothing to move *from*, and an equivalent target has
    // nowhere to move *to*. Both just land.
    if (
      !isSameInstance ||
      scaleRangesAreEquivalent(from, target) ||
      prefersReducedMotion() ||
      !shouldAnimateViewportTransition(from, target)
    ) {
      applyScaleRanges(instance, target)
      return
    }

    const startedAt = performance.now()
    const step = () => {
      // Belt and braces against the destroyed-instance race above: even if a
      // frame outlives its plot, it must not touch one that is no longer live.
      if (uPlotRef.current !== instance) {
        tweenFrameRef.current = null
        return
      }

      const elapsed = performance.now() - startedAt
      const progress = Math.min(1, elapsed / CHART_VIEWPORT_TRANSITION_MS)

      applyScaleRanges(instance, interpolateScaleRanges(from, target, progress))

      if (progress < 1) {
        tweenFrameRef.current = requestAnimationFrame(step)
        return
      }

      tweenFrameRef.current = null
    }

    tweenFrameRef.current = requestAnimationFrame(step)

    return cancelTween
  }, [uPlotData, uPlotScaleRanges, hasDataPoints, uPlotRecreateKey, uPlotRef])

  useEffect(() => {
    const u = uPlotRef.current
    if (!u) return

    // Always re-assert the full appearance (alpha/width/dash/points) here: the
    // series config objects carried over by stableSeries deliberately keep
    // their identity across appearance-only changes (mode toggle, pen
    // highlight, de-emphasis), so the live instance never sees those changes
    // through options. Missing `points` here is what made the lines -> scatter
    // toggle render nothing: width was re-asserted to 0 but the stale
    // `points.show: false` was kept, and stale `points.size` also froze the
    // hover-highlight marker size.
    latestSeriesAppearanceRef.current.forEach((nextAppearance, seriesIndex) => {
      const currentSeries = u.series[seriesIndex] as uPlot.Series & {
        alpha?: number
        width?: number
      }

      if (!currentSeries) return

      currentSeries.alpha = nextAppearance.alpha
      currentSeries.width = nextAppearance.width
      currentSeries.dash = nextAppearance.dash

      const points = currentSeries.points
      if (points) {
        if (nextAppearance.pointsShow !== undefined) {
          // The live instance has normalized `points.show` into a function
          // (uPlot's fnOrSelf) and calls it on every draw, so a raw boolean
          // would throw -- re-assert it as a function.
          const pointsShow = nextAppearance.pointsShow
          points.show = () => pointsShow
        }
        if (nextAppearance.pointsSize !== undefined) {
          // `points.size` stays a plain number on the live series and the
          // point paths are rebuilt from it on every draw.
          points.size = nextAppearance.pointsSize
        }
      }
    })

    logAnnotationDebug('uplot-series-appearance-applied', {
      seriesCount: Math.max(0, u.series.length - 1),
    })
    u.redraw(false, false)
  }, [uPlotRef, uPlotSeriesAppearance, uPlotRecreateKey])

  // Observe ChartArea rather than ChartContainer for resize events.
  // uPlot sets fixed pixel dimensions on its wrapper inside ChartContainer,
  // so watching ChartContainer would create a circular dependency where uPlot
  // content dictates the observed element's size. ChartArea's size is driven
  // by the parent flex layout and is independent of uPlot content.
  useEffect(() => {
    if (!chartAreaRef.current) return

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (uPlotRef.current) {
          const { width, height } = entry.contentRect
          if (width > 0 && height > 0) {
            uPlotRef.current.setSize({ width, height })
          }
        }
      }
    })

    observer.observe(chartAreaRef.current)
    return () => observer.disconnect()
  }, [uPlotRef])

  // Re-arm drag-to-zoom when the chart comes back into view.
  //
  // uPlot caches `over.getBoundingClientRect()` and maps every pointer event
  // (including the native drag-to-zoom select) against that cached rect. It
  // only re-reads the rect on a window resize/scroll, on `setSize`, or when the
  // rect has been explicitly invalidated — never merely because the element was
  // shown again. In the doc-viewer the chart tabs are kept alive and toggled in
  // and out of view, so an instance created (or last measured) while its tab was
  // hidden/off-screen holds a zeroed or stale rect. Drag-to-zoom then reads
  // pointer coordinates against the wrong origin and does nothing usable until
  // something incidental (a real window resize, or a toolbar action that trips
  // the embedder's resize hack) invalidates the rect — which is the "have to
  // click a toolbar button first" symptom.
  //
  // ResizeObserver does not close this gap: a re-show at the same pixel size is
  // not a size change, so it never fires. Visibility is the right signal, so
  // observe it directly and re-apply the live size — `setSize` invalidates the
  // cached rect (uPlot's `syncRect`) even when the dimensions are unchanged.
  useEffect(() => {
    if (!chartAreaRef.current) return
    if (typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver((entries) => {
      const instance = uPlotRef.current
      if (!instance) return
      for (const entry of entries) {
        resyncViewportRectOnVisible(instance, entry)
      }
    })

    observer.observe(chartAreaRef.current)
    return () => observer.disconnect()
  }, [uPlotRef])

  return { chartContainerRef, chartAreaRef }
}
